import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { FirGhostSourcingService } from './fir-ghost-sourcing.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { HttpErrorResponse } from '@angular/common/http';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-fir-ghost-sourcing',
    templateUrl: './fir-ghost-sourcing.component.html',
    styleUrls: ['./fir-ghost-sourcing.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirGhostSourcingComponent implements OnInit, OnDestroy {

  /**
   * Single channel for both confirm and cancel — matches the canonical
   * `fir-form-regen` pattern. `createCustomElement` converts this to a
   * native `request-grid-update` CustomEvent on the host, which the
   * AngularJS bridge already listens for and always calls
   * `$uibModalInstance.close()` (refreshing the grid only on `'refresh'`).
   */
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  showModal: boolean = true;
  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to rerun default sourcing for the selected entities?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  baseURLs: GtwElementsBaseUrlsModel = {
    admin: '',
    api: '',
    custom: '',
    efile: '',
    pdfEngine: '',
    bulkPdfEngine: ''
  };
  /** Minimal shape required by `FirGhostSourcingService.triggerSourcing`. */
  globalService: { filterParams: any; globalParams: any };
  logged_in_user: string = '';
  syncSaveDtls: any[] = [];
  rowData: any;
  gridData: any;

  private unsubscribe$ = new Subject<void>();
  private subscriptions = new Subscription();
  private saveRequestSeq = 0;

  constructor(
    private firGhostSourcingService: FirGhostSourcingService,
    private gtwSnackbarService: GtwSnackbarService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef
  ) {
    this.globalService = { filterParams: {}, globalParams: {} };
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subscriptions.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
    this.subscriptions.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    const apiRoot = gc.api || gc.base_url || '';
    this.baseURLs = {
      admin: gc.admin ?? '',
      api: apiRoot,
      custom: gc.custom ?? '',
      efile: gc.efile ?? '',
      pdfEngine: gc.pdfEngine ?? '',
      bulkPdfEngine: gc.bulkPdfEngine ?? ''
    };
    const baseGlobalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    const filterParams = normalizeFilterParams(activeScreen) || {};
    this.globalService = { filterParams, globalParams: baseGlobalParams };

    const sso = this.gtwApp?.USER_SETTINGS?.user?.sso_id;
    this.logged_in_user = sso != null && sso !== '' ? String(sso) : this.logged_in_user;

    const rowPayload = rowPayloadFromActiveScreen(activeScreen);
    if (Array.isArray(rowPayload) && rowPayload.length > 0) {
      this.rowData = rowPayload;
      this.processRowData();
    } else {
      const sel = activeScreen?.data?.selected;
      const g = gridPayloadFromActiveScreen(activeScreen);
      const gridCandidate = sel != null ? sel : g;
      if (gridCandidate != null && typeof gridCandidate === 'object') {
        this.gridData = gridCandidate;
        this.processGridData();
      }
    }
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  processRowData(): void {
    if (this.rowData && this.rowData.length > 0) {
      this.syncSaveDtls = this.rowData.map((row: any) => {
        return {
          entity_name: row.entity_name,
          entity_ein: row.entity_ein,
          jcd_key: row.jcd_key,
          object_id: row.object_id,
          jcd_ta_key: row.jcd_ta_key
        };
      });
    }
  }

  processGridData() {
    if (this.gridData) {
      this.syncSaveDtls = [];
      const tempGridData = safeJsonInput<any>(JSON.stringify(this.gridData), null, 'fir-ghost-sourcing.parse1');

      if (tempGridData) {
        for (const key in tempGridData) {
          const gridObj = {
            object_id: tempGridData[key].data.object_id,
            combination_key: tempGridData[key].data.COMBINATION_KEY,
            leid: tempGridData[key].data.LEID,
            cdr_number: tempGridData[key].data.CDR_NO
          };
          this.syncSaveDtls.push(gridObj);
        }
      }
    }
  }

  onCancel(): void {
    this.showModal = false;
    this.requestGridUpdate.emit('cancel');
    this.cdr.markForCheck();
  }

  onConfirm(): void {
    try {
      this.modalConfig = { ...this.modalConfig, loading: true };
      this.cdr.markForCheck();

      const returnClobSettingsObj: any = {};
      const ecSaveDtls: any[] = [];
      const ccKeys = this.syncSaveDtls.map(item => item.combination_key);
      const ccKeysString = ccKeys.join(',');

      returnClobSettingsObj['sso_id'] = this.logged_in_user;
      returnClobSettingsObj['combination_key'] = ccKeysString;

      const message = 'Default Sourcing will be regenerated for selected entities';

      this.sendDetails(returnClobSettingsObj, ecSaveDtls, message);
    } catch (_e) {
      this.modalConfig = { ...this.modalConfig, loading: false };
      this.gtwSnackbarService.openSnackBar('Error occurred during ghost sourcing setup', 'gtw-snackbar--red');
      this.cdr.markForCheck();
    }
  }

  private sendDetails(returnClobSettingsObj: any, ecSaveDtls: any[], message: string): void {
    const apiRoot = this.baseURLs.api || (this.baseURLs as any).base_url || '';

    if (!apiRoot) {
      this.modalConfig = { ...this.modalConfig, loading: false };
      this.gtwSnackbarService.openSnackBar('Configuration error: API URL not available', 'gtw-snackbar--red');
      this.cdr.markForCheck();
      return;
    }

    const seq = ++this.saveRequestSeq;
    try {
      this.subscriptions.add(
        this.firGhostSourcingService.triggerSourcing(
          returnClobSettingsObj,
          ecSaveDtls,
          apiRoot,
          this.globalService
        ).pipe(
          takeUntil(this.unsubscribe$)
        ).subscribe({
          next: (result: any) => {
            if (seq !== this.saveRequestSeq) {
              return;
            }
            this.modalConfig = { ...this.modalConfig, loading: false };

            if (result && result.callSuccess === '1') {
              this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
              this.requestGridUpdate.emit('refresh');
            } else {
              let errorMessage = 'Error occurred';
              if (result && result.errorMessage && result.errorMessage !== 'null') {
                errorMessage = result.errorMessage;
              }
              this.gtwSnackbarService.openSnackBar(errorMessage, 'gtw-snackbar--red');
            }
            this.cdr.markForCheck();
          },
          error: (error: HttpErrorResponse) => {
            if (seq !== this.saveRequestSeq) {
              return;
            }
            this.modalConfig = { ...this.modalConfig, loading: false };

            let errorMessage = 'Error occurred';
            if (error.error && error.error.errorMessage) {
              errorMessage = error.error.errorMessage;
            }
            this.gtwSnackbarService.openSnackBar(errorMessage, 'gtw-snackbar--red');
            this.cdr.markForCheck();
          }
        })
      );
    } catch (serviceError: any) {
      this.modalConfig = { ...this.modalConfig, loading: false };
      this.gtwSnackbarService.openSnackBar('Service call error: ' + serviceError.message, 'gtw-snackbar--red');
      this.cdr.markForCheck();
    }
  }
}
